import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFrontendTool } from '@copilotkit/react-core';
import { Card, Pagination, Skeleton, Stack, Text, Title } from '@mantine/core';

import { CamerasWidget as CamerasWidgetType } from 'types/dashboard_widget';
import { Stream } from 'types/stream';

import { useCameras } from 'cameras/hooks/useCameras';
import { useMqttCamerasState } from 'cameras/hooks/useMqttCamerasState';
import { useStreamsByCameras } from 'streams/hooks/useStreamsByCameras';
import { useMqttStreamsState } from 'streams/hooks/useMqttStreamsState';
import { useHasAccess } from 'hooks/useHasAccess';
import { useAPI } from 'hooks/api/useAPI';
import { DeployRoute, NavRoute } from 'application/types/routes';

import { VideoSourceSnapshot } from 'components/VideoSourceSnapshot/VideoSourceSnapshot';

import { AddToDashboardMenu } from './AddToDashboardMenu';

const PAGE_SIZE = 6;

const isH264 = (stream: Stream) =>
  stream.name.toLowerCase().includes('h264') ||
  stream.name.toLowerCase().includes('h.264') ||
  stream.source === 'pipeline_stream';

type ShowCamerasArgs = {
  cameraIds?: string;
  gatewayIds?: string;
  autoPlay?: boolean;
  limit?: number;
};

type ShowCamerasRenderProps = {
  status: 'complete' | 'executing' | 'inProgress';
  args: ShowCamerasArgs;
};

function safeJsonParse<T>(json: string | T | undefined, fallback: T): T {
  if (!json) return fallback;
  if (typeof json !== 'string') return json;
  try {
    return JSON.parse(json) as T;
  } catch {
    return fallback;
  }
}

function CamerasRenderer({ status, args }: ShowCamerasRenderProps) {
  const { applicationID } = useAPI();
  const navigate = useNavigate();
  const [hasAccess] = useHasAccess();
  const [currentPage, setCurrentPage] = useState(1);

  const {
    cameraIds: cameraIdsJson,
    gatewayIds: gatewayIdsJson,
    autoPlay = false,
    limit = PAGE_SIZE,
  } = args;

  const cameraIds: string[] | undefined = safeJsonParse(cameraIdsJson, undefined);
  const gatewayIds: string[] | undefined = safeJsonParse(gatewayIdsJson, undefined);

  const { data: cameras, isLoading } = useCameras({
    params: {
      camera_ids: cameraIds,
      gateway_ids: gatewayIds,
      page: currentPage,
      limit,
    },
    enabled: status === 'complete',
  });

  useMqttCamerasState(cameras?.data ? cameras.data.map(({ id }) => id) : []);

  // Fetch streams for non-virtual cameras (needed for play button and autoPlay)
  const nonVirtualCameras =
    cameras?.data?.filter((camera) => camera.conn_type !== 'virtual') || [];

  const shouldFetchStreams = nonVirtualCameras.length > 0;

  const { streams: allStreams } = useStreamsByCameras({
    cameras: nonVirtualCameras,
    sources: ['camera_stream', 'uri_stream', 'pipeline_stream'],
    stream_types: ['webrtc', 'rtsp'],
    limit: 50,
    enabled: shouldFetchStreams,
  });

  useMqttStreamsState(allStreams.map(({ id }) => id));

  const getStreamForCamera = (cameraId: string) => {
    if (allStreams.length === 0) return null;

    const cameraStreams = allStreams.filter(
      (stream) => stream.camera_id === cameraId
    );

    if (cameraStreams.length === 0) return null;

    const h264Streams = cameraStreams.filter(isH264);
    return h264Streams[0] || cameraStreams[0];
  };

  const createWidget = () => {
    const defaultLayout = { x: 0, y: 0, w: 6, h: 4 };

    return new CamerasWidgetType(
      crypto.randomUUID(),
      'Cameras',
      {
        camera_ids: cameraIds,
        gateway_ids: gatewayIds,
      },
      autoPlay,
      defaultLayout
    );
  };

  if (status === 'inProgress' || status === 'executing' || isLoading) {
    return (
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Text c="dimmed" size="sm">
            Loading cameras...
          </Text>
          <div className="@container">
            <div className="grid grid-cols-1 gap-2 @md:grid-cols-3">
              {Array.from({ length: Math.min(limit, 6) }).map((_, i) => (
                <Skeleton key={i} height={120} radius="md" />
              ))}
            </div>
          </div>
        </Stack>
      </Card>
    );
  }

  const hasMatchingCameras = cameras?.data && cameras.data.length > 0;
  const hasPagination = cameras && cameras.total_pages > 1;

  if (!hasMatchingCameras) {
    return (
      <Card withBorder p="md" radius="md">
        <Text size="sm" c="dimmed">
          No cameras found matching the filter criteria.
        </Text>
      </Card>
    );
  }

  return (
    <Card withBorder p="md" radius="md" className="relative">
      <Stack gap="sm">
        <Title order={5}>
          Cameras ({cameras.data.length})
        </Title>
        <div className="@container">
          <div className="grid grid-cols-1 gap-2 @md:grid-cols-3">
            {cameras.data.map((camera) => {
              if (!camera.id) return null;

              const stream = getStreamForCamera(camera.id);

              return (
                <div
                  key={camera.id}
                  className="overflow-hidden rounded-md border border-neutral-200 dark:border-neutral-700"
                >
                  <VideoSourceSnapshot
                    className="aspect-video w-full"
                    source={stream || camera}
                    camera={camera}
                    autoRefresh
                    showSettingsButton={hasAccess('deploy_edit')}
                    onSettingsClick={() =>
                      navigate(
                        `/applications/${applicationID}/${NavRoute.DEPLOY}/${DeployRoute.CAMERAS}/${camera.id}`,
                        { state: { tab: 'settings' } }
                      )
                    }
                  />
                </div>
              );
            })}
          </div>
        </div>

        {hasPagination && (
          <Pagination
            total={cameras.total_pages}
            value={currentPage}
            onChange={setCurrentPage}
            size="sm"
          />
        )}
      </Stack>
      <div className="absolute bottom-2 right-2">
        <AddToDashboardMenu createWidget={createWidget} />
      </div>
    </Card>
  );
}

export function useShowCamerasTool() {
  useFrontendTool(
    {
      name: 'showCameras',
      description:
        'Display cameras in a grid view. Shows camera snapshots by default. Can filter by specific camera IDs or gateway IDs.',
      parameters: [
        {
          name: 'cameraIds',
          type: 'string',
          description:
            'JSON array of specific camera IDs to display. If not provided, shows all cameras.',
          required: false,
        },
        {
          name: 'gatewayIds',
          type: 'string',
          description:
            'JSON array of gateway IDs to filter cameras by. Only shows cameras connected to these gateways.',
          required: false,
        },
        {
          name: 'autoPlay',
          type: 'boolean',
          description:
            'If true, shows live video streams. If false (default), shows camera snapshots.',
          required: false,
        },
        {
          name: 'limit',
          type: 'number',
          description: 'Maximum number of cameras to display per page. Defaults to 6.',
          required: false,
        },
      ],
      handler: async (args: ShowCamerasArgs) => {
        return { ...args, message: 'Cameras displayed to user' };
      },
      render: ({ status, args }) => (
        <CamerasRenderer status={status} args={args as ShowCamerasArgs} />
      ),
    },
    []
  );
}
